import React from "react";
import { motion } from "framer-motion";
import { Quote, ExternalLink } from "lucide-react";
import { SectionHeader } from "./SectionHeader";
import { REVIEWS_URL } from "@/lib/siteContent";
import { useT } from "@/lib/i18n";

/**
 * Guest reviews collected on megaubytovanie.sk.
 *
 * Shown as attributed quotes only, with no Review/AggregateRating JSON-LD. Google
 * does not allow a business to mark up reviews about itself, so the schema
 * would risk a manual action for no gain; the platform's own page carries it.
 */
export const Testimonials = () => {
  const { t } = useT();
  const section = t.reviewsSection;

  if (!section || !t.reviews?.length) return null;

  return (
    <section id="recenzie" className="section-y bg-secondary/40" aria-labelledby="recenzie-title">
      <div className="container-x">
        <SectionHeader kicker={section.kicker} title={section.title} subtitle={section.subtitle} />

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="reviews-grid">
          {t.reviews.map((r, idx) => (
            <motion.figure
              key={r.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, ease: "easeOut", delay: idx * 0.07 }}
              className="flex flex-col rounded-2xl border border-border bg-card p-6 shadow-soft"
              data-testid={`review-card-${idx}`}
            >
              <div className="flex items-center justify-between gap-3">
                <span
                  className="inline-flex items-baseline gap-1 rounded-lg px-2.5 py-1 text-primary-foreground"
                  style={{ background: "hsl(var(--pine))" }}
                >
                  <span className="font-display text-lg font-semibold leading-none">{r.rating}</span>
                  <span className="text-[11px] opacity-80">{section.ratingLabel}</span>
                </span>
                <Quote className="h-5 w-5 shrink-0 text-muted-foreground/40" aria-hidden />
              </div>

              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground/90">
                {r.text}
              </blockquote>

              <figcaption className="mt-5 border-t border-border/60 pt-4 text-sm">
                <div className="font-medium text-foreground">{r.name}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {r.party} · {r.stay}
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs text-muted-foreground">
          <span>{section.sourceLabel}</span>
          <a
            href={REVIEWS_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-foreground link-underline"
            data-testid="reviews-source-link"
          >
            {section.sourceCta}
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    </section>
  );
};
